import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './ReviewModal.css';
import axios from 'axios';
import { selectUser, createReview, selectReviewState, resetReviewState } from '../redux/WithcookieSlice';
import { useTranslation } from '../redux/TranslationHelper';

const ReviewModal = ({ isOpen, onClose, productId, orderId, onReviewSubmitted }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectUser);
  const reviewState = useSelector(selectReviewState);
  const { t } = useTranslation();
  
  const [reviewData, setReviewData] = useState({
    content: '',
    rating: 5,
    images: []
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successHandled, setSuccessHandled] = useState(false);
  
  // 모달 닫기 핸들러
  const handleClose = () => {
    dispatch(resetReviewState());
    onClose();
  };
  
  useEffect(() => {
    if (isOpen) {
      // 모달이 열릴 때 초기화
      setReviewData({
        content: '',
        rating: 5,
        images: []
      });
      setError(null);
      setSuccessHandled(false);
    }
  }, [isOpen]);

  // reviewState 변경 감지하여 처리
  useEffect(() => {
    if (reviewState.success && !successHandled) {
      setSuccessHandled(true);
      alert(t('리뷰가 등록되었습니다'));
      if (onReviewSubmitted) {
        onReviewSubmitted();
      }
      handleClose();
    }
    
    if (reviewState.error) {
      setError(reviewState.error);
      setIsSubmitting(false);
    }
  }, [reviewState, onReviewSubmitted, successHandled, t]);
  
  // 별점 변경 핸들러
  const handleRatingChange = (rating) => {
    setReviewData(prev => ({ ...prev, rating }));
  };
  
  // 내용 변경 핸들러
  const handleContentChange = (e) => {
    setReviewData(prev => ({ ...prev, content: e.target.value }));
  };
  
  // 이미지 업로드 핸들러
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + reviewData.images.length > 5) {
      setError(t('이미지는 최대 5개까지만 업로드할 수 있습니다.'));
      return;
    }
    
    const promises = files.map(file => uploadImage(file));
    
    Promise.all(promises)
      .then(uploadedImages => {
        setReviewData(prev => ({
          ...prev,
          images: [...prev.images, ...uploadedImages]
        }));
        setError(null);
      })
      .catch(err => {
        setError(t('이미지 업로드 중 오류가 발생했습니다.'));
        console.error(t('이미지 업로드 실패:'), err);
      });
  };
  
  // 이미지 삭제 핸들러
  const handleRemoveImage = (index) => {
    setReviewData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };
  
  // 이미지 업로드 함수
  const uploadImage = (file) => {
    return new Promise(async (resolve, reject) => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('bucket', 'withcookie-bucket');
        formData.append('path', 'c/reviews');

        const res = await axios.post(
          `https://refrigerator.logipasta.com/v1/file`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Access-Control-Allow-Origin': '*',
            },
          }
        );

        if (res.data && res.data.file) {
          resolve(res.data.file);
        } else {
          reject(t('이미지 URL을 가져올 수 없습니다.'));
        }
      } catch (err) {
        console.error(t('이미지 업로드 실패:'), err);
        reject(err);
      }
    });
  };
  
  // 리뷰 제출 함수
  const handleSubmitReview = async () => {
    if (!reviewData.content.trim()) {
      setError(t('리뷰 내용을 입력해주세요.'));
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // withcookieslice의 createReview 액션 사용
      dispatch(createReview({
        productId: productId,
        cartId: orderId,
        rating: reviewData.rating,
        content: reviewData.content
      }));
    } catch (err) {
      console.error(t('리뷰 제출 실패:'), err);
      setError(t('리뷰 제출 중 오류가 발생했습니다.'));
      setIsSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="review-modal-overlay">
      <div className="review-modal">
        <div className="review-modal-header">
          <h2>{t('상품 리뷰 작성')}</h2>
          <button className="close-button" onClick={handleClose}>×</button>
        </div>
        
        <div className="review-modal-body">
          <div className="rating-section">
            <h3>{t('별점')}</h3>
            <div className="star-rating">
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  className={`star ${star <= reviewData.rating ? 'filled' : ''}`}
                  onClick={() => handleRatingChange(star)}
                >
                  ★
                </span>
              ))}
            </div>
          </div>
          
          <div className="content-section">
            <h3>{t('리뷰 내용')}</h3>
            <textarea
              value={reviewData.content}
              onChange={handleContentChange}
              placeholder={t('상품에 대한 경험과 생각을 자유롭게 작성해주세요.')}
            />
          </div>
          
          {/* <div className="image-section">
            <h3>{t('사진 첨부 (최대 5장)')}</h3>
            <div className="image-upload">
              <input
                type="file"
                id="review-image-upload"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                disabled={reviewData.images.length >= 5}
              />
              <label htmlFor="review-image-upload" className={reviewData.images.length >= 5 ? 'disabled' : ''}>
                {t('이미지 선택')}
              </label>
              <span className="image-count">{reviewData.images.length}/5</span>
            </div>
            
            {reviewData.images.length > 0 && (
              <div className="image-preview-container">
                {reviewData.images.map((image, index) => (
                  <div key={index} className="image-preview">
                    <img src={image} alt={t(`리뷰 이미지 ${index + 1}`)} />
                    <button
                      className="remove-image"
                      onClick={() => handleRemoveImage(index)}
                      title={t("이미지 삭제")}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div> */}
          
          {error && <div className="error-message">{error}</div>}
        </div>
        
        <div className="review-modal-footer">
          <button className="cancel-button" onClick={handleClose}>{t('취소')}</button>
          <button 
            className="submit-button" 
            onClick={handleSubmitReview}
            disabled={isSubmitting || reviewState.loading}
          >
            {isSubmitting || reviewState.loading ? t('제출 중...') : t('리뷰 등록')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewModal; 